// DS: TO0.6
import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import TierConsumerReduxModule from '../../Tier/redux/consumer/ModuleLoadable';
import { ButtonSize } from '@wix/da-ds/pkg/Buttons/types';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';

import SubscribeVariant from './variants/Subscribe';
import SubscribedVariant from './variants/Subscribed';
import ViewVariant from './variants/View';
import DisabledVariant from './variants/Disabled';

import s from './SubscribeButton.scss';

export interface Props {
  tier: PapiDeviation;
  /** should display the price per month? */
  withPrice?: boolean;
  withSaleIndicator?: boolean;
  type?: 'subscribe' | 'subscribed' | 'disabled' | 'view';
  /** "Unsubscribe" should instead be View, leading to the subs page */
  showUnsubAsView?: boolean;
  className?: string;
  href?: string;
  size?: ButtonSize;

  reduxReady?: boolean;
  matureFilterOverridden?: boolean;
  matureFilterDisabled?: boolean;
  onUnsubscribe: (tier: PapiDeviation) => void;
}

const SubscribeButton: React.FC<Props> = ({
  tier,
  tier: { tier: { isUserSubscribed, canUserSubscribe } = {} },
  type,
  withPrice,
  withSaleIndicator,
  showUnsubAsView,
  className,
  href,
  size,
  reduxReady,
  matureFilterOverridden,
  matureFilterDisabled,
}) => {
  const { showBlocked } = useDeviationMatureFilter(tier);

  const waitingAction = useRef<any>(undefined);

  useEffect(() => {
    if (reduxReady && waitingAction.current) {
      waitingAction.current();
      waitingAction.current = undefined;
    }
  }, [reduxReady]);

  // TODO: this (or the ds button itself) really needs a focus-visible ring

  const renderViewVariant = () => (
    <ViewVariant
      tier={tier}
      className={cn(className, s['centered'])}
      size={size}
    />
  );

  const renderSubscribeVariant = () => (
    <SubscribeVariant
      tier={tier}
      className={className}
      withPrice={withPrice}
      withSaleIndicator={withSaleIndicator}
      href={href}
      size={size}
    />
  );

  const renderSubscribedVariant = () => (
    <SubscribedVariant
      tier={tier}
      className={cn(className, s['centered'], s['unsubscribe-width'])}
      href={href}
      size={size}
    />
  );

  const renderDisabledVariant = () => (
    <DisabledVariant className={cn(className, s['centered'])} size={size} />
  );

  let buttonElement;

  if (type) {
    switch (type) {
      case 'view':
        buttonElement = renderViewVariant();
        break;
      case 'subscribe':
        buttonElement = renderSubscribeVariant();
        break;
      case 'subscribed':
        buttonElement = renderSubscribedVariant();
        break;
      case 'disabled':
        buttonElement = renderDisabledVariant();
        break;
      default:
        break;
    }
  } else {
    if (isUserSubscribed) {
      if (showUnsubAsView) {
        buttonElement = renderViewVariant();
      } else {
        buttonElement = renderSubscribedVariant();
      }
    } else if (
      (showBlocked && !matureFilterDisabled && !matureFilterOverridden) ||
      !canUserSubscribe
    ) {
      buttonElement = renderDisabledVariant();
    } else {
      buttonElement = renderSubscribeVariant();
    }
  }

  return (
    <>
      {/* Redux module not lazy loaded because its small currently */}
      <TierConsumerReduxModule />
      {buttonElement}
    </>
  );
};
SubscribeButton.displayName = 'SubscribeButton';

export default SubscribeButton;
